<template>
  <div :class="depth ? '' : 'border-t'">
    <div
      v-if="modifiedNodes && !modifiedNodes.length && !depth && !isAddNewHidden && mode == 'edit'">
      <button
        class="p-2 m-2 text-xs ease-in delay-75 transform rounded-md hover:text-white text-slate-400 bg-slate-200 hover:bg-theme-400"
        @click="onAddNode({ action: 'add' })">
        {{ $t('tree.addRoot') }}
      </button>
    </div>
    <div v-if="modifiedNodes">
      <!-- node with tree list -->
      <div
        v-for="(nodeElement, index) in modifiedNodes"
        :key="index"
        :draggable="mode == 'edit'"
        @dragstart.stop="onDragStart($event, nodeElement)"
        @dragover.prevent
        @dragenter.prevent>
        <div
          @dragleave="nodeElement.addChildActive = false"
          @dragover="nodeElement.addChildActive = true"
          @drop.prevent="onDropInto($event, nodeElement)"
          :class="[
            nodeElement.addChildActive ? 'border border-dashed border-theme-500 shadow-md' : '',
          ]">
          <tree-node
            :node="nodeElement"
            :style="indent"
            :edit-mode="mode == 'edit'"
            :main-tree="isMainTree"
            :inline-editing="inlineEditing"
            :options="treeNodeOptions"
            :selected-node-id="selectedNodeId"
            :table="table"
            :has-checkboxes="hasCheckboxes" />
        </div>
        <tree-list-nodes
          v-if="nodeElement.expanded || nodeElement.children.length == 0"
          :nodes="nodeElement.children"
          :inline-editing="inlineEditing"
          :mode="mode"
          :depth="depth + 1"
          :is-main-tree="isMainTree || false"
          :selected-node-id="selectedNodeId"
          :tree-node-options="treeNodeOptions"
          :table="table"
          :has-checkboxes="hasCheckboxes"
          @move-node="onMoveNestedNode"
          @load-more-nodes="(childNode) => emit('loadMoreNodes', childNode)" />

        <div :style="buttonIndent" v-if="nodeElement.expanded && hasMoreNodes(nodeElement)">
          <BaseButton size="sm" color="light" outline @click="emit('loadMoreNodes', nodeElement)">
            {{ `${$t('tree.loadMore')} ${nodeElement.label}` }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, inject } from 'vue';
import TreeNode from './TreeNode.vue';
import BaseButton from './BaseButton.vue';
import type { HandleTreeNodeOnActionProvider, TreeType } from '@/types/ComponentTypes';
import { deepCopy } from '@/utils/data';

const hasMoreNodes = computed(() => {
  return (node: TreeType) => {
    if (node.initialChildrenCount && node.visibleChildrenCount) {
      return node.initialChildrenCount > node.visibleChildrenCount;
    }
    return false;
  };
});

const props = withDefaults(
  defineProps<{
    treeNodeOptions?: {
      hasDeleteOption?: boolean;
      hasAddOption?: boolean;
      hasEditOption?: boolean;
      hasRenameOption?: boolean;
      hasDuplicateOption?: boolean;
      hasCheckboxes?: boolean;
    };
    nodes: TreeType[];
    depth?: number;
    isMainTree?: boolean;
    inlineEditing?: boolean;
    isAddNewHidden?: boolean;
    mode: 'view' | 'edit';
    selectedNodeId?: string;
    table?: boolean;
    hasCheckboxes?: boolean;
  }>(),
  {
    depth: 0,
    hasEditOption: false,
    mode: 'edit',
    table: false,
    hasCheckboxes: false,
  }
);

const emit = defineEmits(['moveNode', 'loadMoreNodes']);

const modifiedNodes = ref<TreeType[]>([]);

onMounted(() => {
  modifiedNodes.value = deepCopy(props.nodes);
});

watch(
  () => props.nodes,
  () => {
    modifiedNodes.value = deepCopy(props.nodes);
  }
);

const indent = computed(() => {
  return { 'padding-left': `${props.depth * 25}px` };
});
const buttonIndent = computed(() => {
  return { 'padding-left': `${props.depth * 40}px` };
});

// INJECT HandleAction
const handleOnAction = inject<HandleTreeNodeOnActionProvider>('handleOnAction', () => {});

function onAddNode(nodeAction: { action: string; node?: TreeType }) {
  handleOnAction(nodeAction);
}

function onDragStart(event: DragEvent, node: TreeType) {
  if (!event.dataTransfer) return;
  event.dataTransfer.dropEffect = 'move';
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('nodeId', node.uid);
}

function onDropInto(event: DragEvent, element: TreeType) {
  if (!event.dataTransfer) return;
  const nodeId = event.dataTransfer.getData('nodeId');
  emit('moveNode', { nodeId, targetNode: element });
  element.addChildActive = false;
}

function onMoveNestedNode(payload: { nodeId: string; targetNode: TreeType }) {
  emit('moveNode', payload);
}
</script>
